import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, Backdrop } from "@mui/material";

export function isExcludedDropTarget(target, selector) {
  if (!selector || !target) return false;
  if (typeof target.closest === "function") {
    try {
      return Boolean(target.closest(selector));
    } catch {
      return false;
    }
  }
  return target.parentElement
    ? isExcludedDropTarget(target.parentElement, selector)
    : false;
}

function getClipboardFiles(clipboardData) {
  if (!clipboardData) return [];

  if (clipboardData.files?.length) {
    return Array.from(clipboardData.files);
  }

  const fromItems = [];
  if (clipboardData.items?.length) {
    for (const item of clipboardData.items) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
}

const FileDropHandler = ({
  onFileUpload,
  children,
  sx,
  dropLabel = "Solte os arquivos para anexar",
  excludeSelector,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [overExcluded, setOverExcluded] = useState(false);
  const containerRef = useRef(null);
  const dragDepthRef = useRef(0);
  const passThroughOverlay = Boolean(excludeSelector);

  const resetDragState = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragging(false);
    setOverExcluded(false);
  }, []);

  const handlePaste = useCallback(
    (e) => {
      try {
        const root = containerRef.current;
        if (!root) return;

        const active = document.activeElement;
        if (!active || !root.contains(active)) return;
        if (isExcludedDropTarget(active, excludeSelector)) return;

        const clipboardData = e.clipboardData || window.clipboardData;
        const fileList = getClipboardFiles(clipboardData);
        if (!fileList.length) return;

        e.preventDefault();
        onFileUpload(fileList.length === 1 ? fileList[0] : fileList);
      } catch (error) {
        console.error("Erro no handlePaste:", error);
      }
    },
    [onFileUpload, excludeSelector]
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
      setIsDragging(true);
      setOverExcluded(isExcludedDropTarget(event.target, excludeSelector));
    },
    [excludeSelector]
  );

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragging(false);
      setOverExcluded(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event) => {
      try {
        const excluded = isExcludedDropTarget(event.target, excludeSelector);
        resetDragState();
        if (excluded) return;

        event.preventDefault();
        event.stopPropagation();

        const dataTransfer = event.dataTransfer;
        if (!dataTransfer?.files?.length) return;

        const files = Array.from(dataTransfer.files);
        if (files.length > 0) {
          onFileUpload(files.length === 1 ? files[0] : files);
        }
      } catch (error) {
        console.error("Erro ao processar o evento de arrastar e soltar:", error);
      }
    },
    [onFileUpload, excludeSelector, resetDragState]
  );

  const handleDropCapture = useCallback(() => {
    resetDragState();
  }, [resetDragState]);

  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);

  const showOverlay = isDragging && !overExcluded;

  return (
    <Box
      ref={containerRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDropCapture={handleDropCapture}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 0,
        ...sx,
      }}
    >
      {children}
      <Backdrop
        open={showOverlay}
        onDragEnter={passThroughOverlay ? undefined : handleDragEnter}
        onDragOver={passThroughOverlay ? undefined : handleDragOver}
        onDragLeave={passThroughOverlay ? undefined : handleDragLeave}
        onDrop={passThroughOverlay ? undefined : handleDrop}
        sx={{
          zIndex: 13000,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: passThroughOverlay ? "none" : "auto",
        }}
      >
        <Typography variant="h6">{dropLabel}</Typography>
      </Backdrop>
    </Box>
  );
};

export default FileDropHandler;
